// Response shapes of central-api `/api/v1` (snake_case, UTC epoch ms).
// zod/mini keeps the validation code small in the bundle. Objects are not
// strict: the UI ignores fields it does not know, so the API can add fields.
import { z } from 'zod/mini';

const nullableNumber = z.nullable(z.number());
const nullableString = z.nullable(z.string());

// --- roles (ADR 0010) -------------------------------------------------------------

export const TENANT_ROLES = ['viewer', 'operator', 'tenant_admin'] as const;
export type TenantRole = (typeof TENANT_ROLES)[number];

/** What the caller may do somewhere (`GET /auth/me`); the API checks each request anyway. */
export const permissions = z.object({
  platform: z.boolean(),
  read: z.boolean(),
  operate: z.boolean(),
  tenant_admin: z.boolean(),
});
export type Permissions = z.infer<typeof permissions>;

/** Before ADR 0010 every user could do everything. */
export const ALL_PERMISSIONS: Permissions = {
  platform: true,
  read: true,
  operate: true,
  tenant_admin: true,
};

/** A tenant role of the signed-in user. */
export const myTenant = z.object({
  id: z.string(),
  name: z.string(),
  role: z.enum(TENANT_ROLES),
  /** null = every site of the tenant. */
  site_ids: z.nullable(z.array(z.string())),
});
export type MyTenant = z.infer<typeof myTenant>;

export const user = z.object({
  id: z.string(),
  email: z.string(),
  /** `platform_admin` = platform staff; null = tenant user. Absent from older APIs. */
  platform_role: z.optional(z.nullable(z.string())),
  tenants: z.optional(z.array(myTenant)),
});
/** The signed-in user with what it may do. */
export type User = z.infer<typeof user> & { permissions: Permissions };
export const userResponse = z.object({ user });
export const meResponse = z.object({
  user: z.nullable(user),
  permissions: z.optional(permissions),
});

export const site = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string(),
  sparkplug_group_id: z.string(),
  timezone: z.string(),
  tenant: z.object({ id: z.string(), name: z.string() }),
  edges: z.number(),
  points: z.number(),
});
export type Site = z.infer<typeof site>;

export const edgeStatus = z.enum(['online', 'offline', 'unknown']);
export type EdgeStatus = z.infer<typeof edgeStatus>;

export const healthValue = z.union([z.number(), z.string(), z.boolean(), z.null()]);
export type HealthValue = z.infer<typeof healthValue>;

export const edge = z.object({
  id: z.string(),
  edge_node_id: z.string(),
  status: edgeStatus,
  last_seen: nullableNumber,
  agent_version: nullableString,
  config_version: nullableNumber,
  health: z.nullable(z.record(z.string(), healthValue)),
  health_ts: nullableNumber,
  devices: z.number(),
  points: z.number(),
  // Lifecycle (WP3): optional so an older API still parses.
  name: z.optional(nullableString),
  /** `lorawan` = the site's virtual LoRaWAN unit (WP9); missing = `edge`. */
  kind: z.optional(z.string()),
  /** Current certificate; null = never enrolled. */
  cert_serial: z.optional(nullableString),
  cert_expires_at: z.optional(nullableNumber),
  /** Expiry of the newest unused enrollment token. */
  enrollment_token_expires_at: z.optional(nullableNumber),
  /** Set while the edge is disabled. */
  disabled_at: z.optional(nullableNumber),
  /** Management VPN node; null = no VPN configured (the UI hides it). */
  vpn: z.optional(
    z.nullable(z.object({ online: z.boolean(), ip: nullableString, last_seen: nullableNumber })),
  ),
});
export type Edge = z.infer<typeof edge>;
export type EdgeVpn = NonNullable<Edge['vpn']>;

export const liveValue = z.object({
  point_id: z.string(),
  ts: z.number(),
  v: nullableNumber,
  vs: nullableString,
  /** 192 good, 64 uncertain, 0 bad. */
  q: z.number(),
  /** BACnet status flags: 1 in-alarm, 2 fault, 4 overridden, 8 out-of-service. */
  sf: nullableNumber,
});
export type LiveValue = z.infer<typeof liveValue>;

export const point = z.object({
  id: z.string(),
  point_key: z.string(),
  metric_name: z.string(),
  name: z.string(),
  description: z.string(),
  unit: z.string(),
  /** 'Float' | 'Boolean' | 'UInt32' today; other Sparkplug types later. */
  data_type: z.string(),
  object_type: nullableString,
  object_instance: nullableNumber,
  /** Siemens proprietary property 3121: description path from the plant view root. */
  siemens_3121_path: z.optional(z.nullable(z.array(z.string()))),
  /** Siemens proprietary property 4397: the same path on PXC4/5/7. */
  siemens_4397_path: z.optional(z.nullable(z.array(z.string()))),
  /** Tags as `key=value`, sorted (absent from older APIs). */
  tags: z.optional(z.array(z.string())),
  /** Limits of an analog point; null = none. */
  min_value: z.optional(nullableNumber),
  max_value: z.optional(nullableNumber),
  device: z.object({
    id: z.string(),
    sparkplug_device_id: z.string(),
    name: z.optional(nullableString),
  }),
  edge: z.object({ id: z.string(), edge_node_id: z.string(), name: z.optional(nullableString) }),
  site: z.object({ id: z.string(), name: z.string(), group_id: z.optional(z.string()) }),
  live: z.nullable(liveValue),
});
export type Point = z.infer<typeof point>;

export function itemList<T extends z.core.$ZodType>(item: T) {
  return z.object({ items: z.array(item) });
}
export const siteList = itemList(site);
export const edgeList = itemList(edge);
export const pointList = z.object({ items: z.array(point), total: z.number() });
export type PointList = z.infer<typeof pointList>;

export const rawSample = z.object({
  ts: z.number(),
  v: nullableNumber,
  vs: nullableString,
  q: z.number(),
  sf: nullableNumber,
});
export type RawSample = z.infer<typeof rawSample>;

export const bucket = z.object({
  ts: z.number(),
  min: nullableNumber,
  max: nullableNumber,
  avg: nullableNumber,
  last: nullableNumber,
  count: z.number(),
});
export type Bucket = z.infer<typeof bucket>;

export const bucketResolution = z.enum(['1m', '15m', '1h']);
export type BucketResolution = z.infer<typeof bucketResolution>;
export type Resolution = 'raw' | BucketResolution;
export type ResolutionParam = 'auto' | Resolution;

export const valuesResponse = z.discriminatedUnion('resolution', [
  z.object({ resolution: z.literal('raw'), points: z.array(rawSample) }),
  z.object({ resolution: bucketResolution, points: z.array(bucket) }),
]);
export type ValuesResponse = z.infer<typeof valuesResponse>;

export const errorBody = z.object({ error: z.string() });

export interface PointQuery {
  site_id?: string | undefined;
  edge_id?: string | undefined;
  device_id?: string | undefined;
  q?: string | undefined;
  /** Tag filters (AND): `key=value`, or `key` for any value. */
  tag?: readonly string[] | undefined;
  limit?: number | undefined;
  offset?: number | undefined;
}

/** `GET /tags`: tags in use with their point counts. */
export const tagCount = z.object({
  tag: z.string(),
  key: z.string(),
  value: z.string(),
  count: z.number(),
});
export type TagCount = z.infer<typeof tagCount>;
export const tagList = itemList(tagCount);

export interface TagQuery {
  site_id?: string | undefined;
  edge_id?: string | undefined;
  key?: string | undefined;
}

/** Tags and limits of a point after an edit (`PATCH /points/:id`, `POST /points/bulk`). */
export const pointEdit = z.object({
  id: z.string(),
  tags: z.array(z.string()),
  min_value: nullableNumber,
  max_value: nullableNumber,
});
export type PointEdit = z.infer<typeof pointEdit>;
export const bulkPointEdit = z.object({ updated: z.number(), items: z.array(pointEdit) });

/** `PATCH /points/:id`: tags replace the point's tags; undefined fields stay. */
export interface PointEditBody {
  tags?: string[];
  min_value?: number | null;
  max_value?: number | null;
}

/** `POST /points/bulk`. */
export interface BulkPointEditBody {
  point_ids: string[];
  /** Set these; another value of the same key is replaced. */
  add_tags?: string[];
  /** `key=value` removes that tag, `key` every value of the key. */
  remove_tags?: string[];
  min_value?: number | null;
  max_value?: number | null;
}

/** `GET /points/live`: current values of many points; `ts` null = none yet. */
export const bulkLive = z.object({
  items: z.array(
    z.object({
      point_id: z.string(),
      ts: nullableNumber,
      v: nullableNumber,
      vs: nullableString,
      q: nullableNumber,
      sf: nullableNumber,
    }),
  ),
});

// Point selection (WP3): discovered BACnet objects of an edge, the selection
// sent to it as config versions, and discovery runs.

/** §4 acquisition modes (`ACQUISITION_MODES` in @kalvio/driver-api). */
export const acquisitionMode = z.enum(['cov', 'poll', 'cov+poll']);
export type AcquisitionMode = z.infer<typeof acquisitionMode>;

export const discoveryRunState = z.enum(['requested', 'running', 'completed', 'failed']);
export type DiscoveryRunState = z.infer<typeof discoveryRunState>;

export const discoveryRun = z.object({
  id: z.string(),
  state: discoveryRunState,
  item_count: z.number(),
  error: nullableString,
  created_at: z.number(),
  started_at: nullableNumber,
  finished_at: nullableNumber,
});
export type DiscoveryRun = z.infer<typeof discoveryRun>;
export const discoveryRunList = itemList(discoveryRun);
export const discoveryRunResponse = z.object({ run: discoveryRun });

export const discoveredDevice = z.object({
  device_id: z.string(),
  instance: z.number(),
  address: nullableString,
  name: nullableString,
  vendor: nullableString,
  model: nullableString,
  object_count: z.number(),
  /** When the run that found the device finished. */
  scanned_at: z.optional(nullableNumber),
  /** From discovery (Protocol_Services_Supported); null = unknown. */
  subscribe_cov: z.optional(z.nullable(z.boolean())),
  subscribe_cov_property: z.optional(z.nullable(z.boolean())),
  /** Active_COV_Subscriptions at discovery time (all subscribers); null = unknown. */
  active_cov_subscriptions: z.optional(nullableNumber),
  /** The edge driver's COV states for this device (health metric); null = none reported. */
  cov_live: z.optional(
    z.nullable(
      z.object({
        active: z.number(),
        pending: z.number(),
        rejected: z.number(),
        limit: z.number(),
        failed: z.number(),
      }),
    ),
  ),
  cov_live_ts: z.optional(nullableNumber),
});
export type DiscoveredDevice = z.infer<typeof discoveredDevice>;

export const edgeObject = z.object({
  device_id: z.string(),
  /** Sparkplug metric name, the protocol address: `AI:1/presentValue`. */
  metric: z.string(),
  /** AI, AO, AV, BI, BO, BV, MI, MO, MV. */
  object_type: z.string(),
  object_instance: z.number(),
  object_name: nullableString,
  description: nullableString,
  /** Siemens proprietary property 3121: description path from the plant view root. */
  siemens_3121_path: z.optional(z.nullable(z.array(z.string()))),
  /** Siemens proprietary property 4397: the same path on PXC4/5/7. */
  siemens_4397_path: z.optional(z.nullable(z.array(z.string()))),
  units: nullableString,
  present_value: z.union([z.number(), z.boolean(), z.null()]),
  commandable: z.boolean(),
  /** The current desired selection; for unselected objects mode and interval are suggestions. */
  selected: z.boolean(),
  mode: acquisitionMode,
  interval_ms: nullableNumber,
  /** The point in central, null if the object was never transferred. */
  point_id: nullableString,
  /** Tags and limits of the point (empty / null without a point). */
  tags: z.optional(z.array(z.string())),
  min_value: z.optional(nullableNumber),
  max_value: z.optional(nullableNumber),
});
export type EdgeObject = z.infer<typeof edgeObject>;

export const edgeObjects = z.object({
  edge: z.object({ id: z.string(), edge_node_id: z.string(), site_id: z.string() }),
  /** The latest discovery run with items, null before the first one. */
  run: z.nullable(discoveryRun),
  /** Selection version shown (newest not rejected); send it back as `base_version`. */
  config_version: z.optional(nullableNumber),
  devices: z.array(discoveredDevice),
  items: z.array(edgeObject),
});
export type EdgeObjects = z.infer<typeof edgeObjects>;

export const configStatus = z.enum(['sent', 'applied', 'rejected']);
export type ConfigStatus = z.infer<typeof configStatus>;

export const configVersion = z.object({
  version: z.number(),
  status: configStatus,
  error: nullableString,
  point_count: z.number(),
  created_at: z.number(),
  sent_at: nullableNumber,
  applied_at: nullableNumber,
});
export type ConfigVersion = z.infer<typeof configVersion>;

export const edgeConfig = z.object({
  /** The newest version. */
  current: z.nullable(configVersion),
  /** Newest first, at most 10. */
  history: z.array(configVersion),
});
export type EdgeConfig = z.infer<typeof edgeConfig>;

/** One selected point in `PUT /edges/:id/config`. */
export interface ConfigPoint {
  device_id: string;
  metric: string;
  mode: AcquisitionMode;
  interval_ms: number | null;
}

export interface ConfigBody {
  points: ConfigPoint[];
  /** Required by the API when the selection stops many points (`needsRemovalConfirm`). */
  confirm_removal?: boolean;
  /** The version the edit is based on; 409 `stale_selection` when another one was saved. */
  base_version?: number | null;
}

export interface DiscoveryScope {
  low_instance?: number;
  high_instance?: number;
}

export const edgeUpdateStatus = z.enum(['sent', 'updated', 'rolled_back', 'failed']);
export type EdgeUpdateStatus = z.infer<typeof edgeUpdateStatus>;

/** An agent update sent to an edge (update/req) and the edge's result. */
export const edgeUpdate = z.object({
  id: z.string(),
  image: z.string(),
  tag: z.string(),
  status: edgeUpdateStatus,
  previous_tag: nullableString,
  error: nullableString,
  created_at: z.number(),
  finished_at: nullableNumber,
});
export type EdgeUpdate = z.infer<typeof edgeUpdate>;

export const edgeUpdates = z.object({
  agent_version: nullableString,
  /** Newest first. */
  items: z.array(edgeUpdate),
});
export type EdgeUpdates = z.infer<typeof edgeUpdates>;

/** Agent versions available in the registry (newest tag names first). */
export const edgeImages = z.object({ image: z.string(), tags: z.array(z.string()) });
export type EdgeImages = z.infer<typeof edgeImages>;

// --- API tokens (`/api-tokens`) -------------------------------------------------

export const API_SCOPES = ['read', 'control:write', 'admin'] as const;
export type ApiScope = (typeof API_SCOPES)[number];

/** Point restriction of a token as the token list shows it. */
export const pointAccessSummary = z.object({
  mode: z.string(),
  /** `list`: number of points. */
  count: z.optional(z.number()),
  /** `enabled`: number of excepted points. */
  except_count: z.optional(z.number()),
  /** `tags`: the tags (any of them). */
  tags: z.optional(z.array(z.string())),
});
export type PointAccessSummary = z.infer<typeof pointAccessSummary>;

/** Point restriction of a new token (`POST /api-tokens`). */
export type PointAccess =
  | { mode: 'list'; ids: string[] }
  | { mode: 'enabled'; except: string[] }
  | { mode: 'tags'; tags: string[] };

const apiTokenShape = {
  id: z.string(),
  name: z.string(),
  /** Plain strings: the list must not break when the API adds a scope. */
  scopes: z.array(z.string()),
  created_at: z.number(),
  expires_at: nullableNumber,
  last_used_at: nullableNumber,
  revoked_at: nullableNumber,
  /** Tenant of the token. */
  tenant_id: z.optional(z.string()),
  /** Platform token (platform staff): every tenant. */
  platform: z.optional(z.boolean()),
  /** Data restriction; null (or absent from older APIs) = all sites. */
  access: z.optional(
    z.nullable(
      z.object({
        site_ids: z.optional(z.array(z.string())),
        /** Point restriction, summarised (the API does not list every id). */
        points: z.optional(pointAccessSummary),
      }),
    ),
  ),
};
export const apiToken = z.object(apiTokenShape);
export type ApiToken = z.infer<typeof apiToken>;
/** Newest first. */
export const apiTokenList = z.object({ items: z.array(apiToken) });

/** The create response: the token with its secret, which is shown only once. */
export const createdApiToken = z.object({ ...apiTokenShape, token: z.string() });
export type CreatedApiToken = z.infer<typeof createdApiToken>;

export interface CreateApiTokenBody {
  name: string;
  scopes: ApiScope[];
  /** Omitted = never expires. */
  expires_in_days?: number;
  /** Restricts the token to these sites (read routes only); omitted = all sites. */
  site_ids?: string[];
  /** Tenant of the token; platform staff omit it (and site_ids) for a platform token. */
  tenant_id?: string;
  /** Restricts the token to points (read scope only); omitted = all points of its sites. */
  points?: PointAccess;
}

// --- users (`/users`, `/auth/password`) ------------------------------------------

/** An account as admins see it (never a password hash). */
export const managedUser = z.object({
  id: z.string(),
  email: z.string(),
  name: nullableString,
  disabled: z.boolean(),
  last_login_at: nullableNumber,
  created_at: z.number(),
  /** `platform_admin` = platform staff; null = tenant user. */
  platform_role: z.nullable(z.string()),
  /** Tenant roles (tenant admins see only their tenants' rows). */
  tenants: z.array(
    z.object({
      tenant_id: z.string(),
      tenant_name: z.string(),
      role: z.enum(TENANT_ROLES),
      site_ids: z.nullable(z.array(z.string())),
    }),
  ),
  /** Sessions that have not expired. */
  active_sessions: z.number(),
});
export type ManagedUser = z.infer<typeof managedUser>;
export const managedUserList = z.object({ items: z.array(managedUser) });

/** A tenant role as the API takes it. */
export interface TenantRoleInput {
  tenant_id: string;
  role: TenantRole;
  /** Omitted or null = every site of the tenant. */
  site_ids?: string[] | null;
}

export interface CreateUserBody {
  email: string;
  name?: string;
  password: string;
  /** Platform staff only. */
  platform_role?: 'platform_admin' | null;
  tenants?: TenantRoleInput[];
}

export interface UpdateUserBody {
  name?: string | null;
  disabled?: boolean;
  platform_role?: 'platform_admin' | null;
  /** Replaces the roles in the tenants the caller administers. */
  tenants?: TenantRoleInput[];
}

// --- tenants (`/tenants`, platform staff) -----------------------------------------

export const tenantInfo = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string(),
  created_at: z.number(),
  sites: z.array(z.object({ id: z.string(), name: z.string() })),
  users: z.number(),
});
export type TenantInfo = z.infer<typeof tenantInfo>;
export const tenantList = z.object({ items: z.array(tenantInfo) });

export const alarmSeverity = z.enum(['critical', 'warning']);
export type AlarmSeverity = z.infer<typeof alarmSeverity>;

/** An alarm event raised by central-worker (`GET /alarms`). */
export const alarm = z.object({
  id: z.string(),
  /** Rule key of a system alarm, e.g. `edge-offline`. */
  rule: nullableString,
  name: z.string(),
  severity: alarmSeverity,
  message: z.string(),
  details: z.record(z.string(), z.unknown()),
  value: nullableNumber,
  raised_at: z.number(),
  /** null = still open. */
  cleared_at: nullableNumber,
  acked_at: nullableNumber,
  acked_by: z.nullable(z.object({ id: z.string(), email: nullableString })),
  site: z.nullable(z.object({ id: z.string(), name: nullableString })),
  edge: z.nullable(z.object({ id: z.string(), edge_node_id: nullableString })),
});
export type Alarm = z.infer<typeof alarm>;

export const alarmList = z.object({
  /** Open first (critical first), then newest first. */
  items: z.array(alarm),
  /** Open alarms (in the site filter). */
  open: z.number(),
  open_critical: z.number(),
  open_unacked: z.number(),
});
export type AlarmList = z.infer<typeof alarmList>;

export interface AlarmQuery {
  state?: 'open' | 'all';
  site_id?: string;
  limit?: number;
}

// Edge lifecycle (WP3): create, enrollment tokens, VPN keys, disable/enable.

export interface CreateEdgeBody {
  group_id: string;
  edge_node_id: string;
  name?: string;
  /** Only used when the group (site) is new. */
  site_name?: string;
}

export const createdEdge = z.object({
  id: z.string(),
  site_id: z.string(),
  group_id: z.string(),
  edge_node_id: z.string(),
});
export type CreatedEdge = z.infer<typeof createdEdge>;

/** One-time enrollment token; the API shows it only in this response. */
export const enrollmentToken = z.object({
  token: z.string(),
  expires_at: z.number(),
  install_command: z.string(),
});
export type EnrollmentToken = z.infer<typeof enrollmentToken>;

export const vpnKey = z.object({ login_server: z.string(), auth_key: z.string() });
export type VpnKey = z.infer<typeof vpnKey>;

export const lifecycleStep = z.object({
  step: z.string(),
  status: z.enum(['done', 'skipped', 'failed']),
  detail: nullableString,
});
export type LifecycleStep = z.infer<typeof lifecycleStep>;

export const lifecycleResult = z.object({
  id: z.string(),
  edge_node_id: z.string(),
  disabled_at: nullableNumber,
  complete: z.boolean(),
  steps: z.array(lifecycleStep),
});
export type LifecycleResult = z.infer<typeof lifecycleResult>;

// --- LoRaWAN (WP9) -----------------------------------------------------------

export const lorawanApplication = z.object({
  application_id: z.string(),
  name: z.string(),
  description: z.string(),
  tenant_id: nullableString,
  tenant_name: nullableString,
  site: z.nullable(z.object({ id: z.string(), name: z.string() })),
  /** False: mapped but ChirpStack no longer lists it; null = not asked. */
  in_chirpstack: z.nullable(z.boolean()),
});
export type LorawanApplication = z.infer<typeof lorawanApplication>;

export const lorawanApplicationList = z.object({
  chirpstack: z.boolean(),
  chirpstack_error: nullableString,
  items: z.array(lorawanApplication),
});
export type LorawanApplicationList = z.infer<typeof lorawanApplicationList>;

export const lorawanDevice = z.object({
  /** Kalvio device id; null until the first uplink. */
  id: nullableString,
  dev_eui: z.string(),
  name: nullableString,
  description: nullableString,
  profile: nullableString,
  /** ChirpStack device profile id; null = ChirpStack not asked. */
  profile_id: nullableString,
  application_id: nullableString,
  last_uplink: nullableNumber,
  /** Percent. */
  battery: nullableNumber,
  /** dBm. */
  rssi: nullableNumber,
  /** dB. */
  snr: nullableNumber,
  points: z.number(),
  in_chirpstack: z.nullable(z.boolean()),
  /** False = disabled in ChirpStack; null = not asked. */
  enabled: z.nullable(z.boolean()),
  join_state: z.nullable(z.enum(['joined', 'pending'])),
});
export type LorawanDevice = z.infer<typeof lorawanDevice>;

export const siteLorawan = z.object({
  edge_id: nullableString,
  chirpstack: z.boolean(),
  chirpstack_error: nullableString,
  /** Application sensors added in Kalvio go into; null = not set up yet. */
  managed_application_id: nullableString,
  applications: z.array(lorawanApplication),
  devices: z.array(lorawanDevice),
});
export type SiteLorawan = z.infer<typeof siteLorawan>;

// LoRaWAN provisioning (admin): ChirpStack setup, profiles, devices, gateways.

const idName = z.object({ id: z.string(), name: z.string() });
export const lorawanSetup = z.object({
  chirpstack: z.boolean(),
  tenant: z.nullable(idName),
  application: z.nullable(idName),
});
export type LorawanSetup = z.infer<typeof lorawanSetup>;

export const lorawanCatalogProfile = z.object({
  key: z.string(),
  name: z.string(),
  vendor: z.string(),
  description: z.string(),
  codec: z.string(),
  /** Installed in the site's ChirpStack tenant; null = installed on first use. */
  profile_id: nullableString,
});
export type LorawanCatalogProfile = z.infer<typeof lorawanCatalogProfile>;

export const lorawanProfile = z.object({
  id: z.string(),
  name: z.string(),
  region: z.string(),
  mac_version: z.string(),
  supports_otaa: z.boolean(),
  catalog: nullableString,
});
export type LorawanProfile = z.infer<typeof lorawanProfile>;

export const lorawanProfiles = z.object({
  catalog: z.array(lorawanCatalogProfile),
  profiles: z.array(lorawanProfile),
});
export type LorawanProfiles = z.infer<typeof lorawanProfiles>;

/** A device as ChirpStack has it; the AppKey is write-only and never part of it. */
export const lorawanDeviceDetail = z.object({
  dev_eui: z.string(),
  join_eui: z.string(),
  name: z.string(),
  description: z.string(),
  application_id: z.string(),
  profile_id: z.string(),
  enabled: z.boolean(),
  last_seen: nullableNumber,
});
export type LorawanDeviceDetail = z.infer<typeof lorawanDeviceDetail>;

export interface CreateLorawanDeviceBody {
  dev_eui: string;
  join_eui: string;
  app_key: string;
  name: string;
  description?: string;
  /** Catalog key or ChirpStack device profile id. */
  profile: string;
  enabled?: boolean;
}

export interface UpdateLorawanDeviceBody {
  name?: string;
  description?: string;
  profile?: string;
  join_eui?: string;
  app_key?: string;
  enabled?: boolean;
}

const gatewayLocation = z.object({
  latitude: z.number(),
  longitude: z.number(),
  altitude: z.number(),
});
export type GatewayLocation = z.infer<typeof gatewayLocation>;

export const lorawanGateway = z.object({
  gateway_eui: z.string(),
  name: z.string(),
  description: z.string(),
  location: z.nullable(gatewayLocation),
  last_seen: nullableNumber,
  /** null = ChirpStack could not be asked. */
  state: z.nullable(z.enum(['online', 'offline', 'never_seen'])),
  in_chirpstack: z.nullable(z.boolean()),
});
export type LorawanGateway = z.infer<typeof lorawanGateway>;

export const lorawanGatewayList = z.object({
  chirpstack_error: nullableString,
  items: z.array(lorawanGateway),
});
export type LorawanGatewayList = z.infer<typeof lorawanGatewayList>;

export interface LorawanGatewayBody {
  gateway_eui?: string;
  name?: string;
  description?: string;
  location?: { latitude: number; longitude: number; altitude?: number } | null;
}

// Rules (WP6, ADR 0003). The definition mirrors the contracts `ruleDefinition`
// (packages/contracts/src/rules.ts); the API validates it with the strict
// schema the edge uses, so the UI omits optional fields instead of sending
// empty values.

export type CompareOp = '>' | '>=' | '<' | '<=';
export interface TimeWindow {
  start: string;
  end: string;
}
export interface WeeklyWindow extends TimeWindow {
  /** ISO weekdays, 1 = Monday … 7 = Sunday. */
  days: number[];
}
export interface ThresholdCondition {
  type: 'threshold';
  point: string;
  op: CompareOp;
  value: number;
  hysteresis?: number | undefined;
  for_s?: number | undefined;
  off_for_s?: number | undefined;
}
export interface EqualsCondition {
  type: 'equals';
  point: string;
  value: number | boolean;
  for_s?: number | undefined;
  off_for_s?: number | undefined;
}
export interface ChangeCondition {
  type: 'change';
  point: string;
  min_delta?: number | undefined;
}
export interface ScheduleCondition {
  type: 'schedule';
  weekly: WeeklyWindow[];
  exceptions?: { date: string; windows: TimeWindow[] }[];
  /** Omitted = holidays run the weekly program; [] = off on holidays. */
  holiday_windows?: TimeWindow[];
}
export type LeafCondition =
  ThresholdCondition | EqualsCondition | ChangeCondition | ScheduleCondition;
export type RuleCondition =
  | LeafCondition
  | { type: 'all'; of: RuleCondition[] }
  | { type: 'any'; of: RuleCondition[] }
  | { type: 'not'; of: RuleCondition };

export type RuleScalar = number | boolean | null;
export type RuleAction =
  | { type: 'raise_event'; severity: AlarmSeverity; message: string }
  | { type: 'clear_event' }
  | { type: 'set_virtual'; point: string; value: RuleScalar }
  /** Dry-run until WP4: the edge logs "would write", never writes. */
  | { type: 'write'; point: string; value: RuleScalar; priority?: number | undefined };

export type VirtualObjectType = 'AV' | 'BV' | 'MV';
export interface RuleOutputDef {
  key: string;
  object_type: VirtualObjectType;
  name: string;
  description?: string | undefined;
  units?: string | undefined;
  initial?: RuleScalar | undefined;
}

export interface RuleDefinition {
  condition: RuleCondition;
  on_true: RuleAction[];
  on_false: RuleAction[];
  min_on_s?: number | undefined;
  min_off_s?: number | undefined;
  fallback?: 'hold' | 'off' | 'on' | undefined;
  outputs: RuleOutputDef[];
}

// Checked only for its outline; the API owns the full validation.
const ruleDefinitionShape = z.custom<RuleDefinition>(
  (v: unknown) =>
    typeof v === 'object' &&
    v !== null &&
    typeof (v as { condition?: unknown }).condition === 'object' &&
    Array.isArray((v as { on_true?: unknown }).on_true) &&
    Array.isArray((v as { on_false?: unknown }).on_false),
);

export const ruleType = z.enum(['schedule', 'threshold', 'equals', 'change', 'combined']);
export type RuleType = z.infer<typeof ruleType>;

export const ruleStatus = z.object({
  version: z.number(),
  state: z.nullable(z.boolean()),
  since: nullableNumber,
  last_eval: nullableNumber,
  last_fire: nullableNumber,
  fires: z.number(),
  error: nullableString,
});
export type RuleStatus = z.infer<typeof ruleStatus>;

export const ruleOutput = z.object({
  key: z.string(),
  point_key: z.string(),
  point_id: nullableString,
  object_type: z.enum(['AV', 'BV', 'MV']),
  /** Instance on device `rules`; null only before central stored the point. */
  instance: nullableNumber,
  name: z.string(),
});
export type RuleOutput = z.infer<typeof ruleOutput>;

const ruleShape = {
  id: z.string(),
  site_id: z.string(),
  edge_id: z.string(),
  edge_node_id: z.string(),
  name: z.string(),
  description: nullableString,
  type: ruleType,
  enabled: z.boolean(),
  version: z.number(),
  definition: ruleDefinitionShape,
  outputs: z.array(ruleOutput),
  /** From the edge's `Rules/Status`; null = not reported (yet). */
  status: z.nullable(ruleStatus),
  /** The edge runs this version (or the rule is disabled and absent). */
  applied: z.boolean(),
  created_at: z.number(),
  updated_at: z.number(),
};
export const rule = z.object(ruleShape);
export type Rule = z.infer<typeof rule>;

export const ruleVersionInfo = z.object({
  version: z.number(),
  created_at: z.number(),
  created_by: nullableString,
});
export type RuleVersionInfo = z.infer<typeof ruleVersionInfo>;
export const ruleDetail = z.object({ ...ruleShape, versions: z.array(ruleVersionInfo) });
export type RuleDetail = z.infer<typeof ruleDetail>;

export const ruleBundleStatus = z.enum(['pending', 'sent', 'applied', 'rejected']);
export type RuleBundleStatus = z.infer<typeof ruleBundleStatus>;

export const ruleEdge = z.object({
  id: z.string(),
  edge_node_id: z.string(),
  name: nullableString,
  status: edgeStatus,
  /** Bundle version the edge acknowledged as applied. */
  rules_version: nullableNumber,
  /** Newest bundle stored for the edge. */
  bundle: z.nullable(
    z.object({
      version: z.number(),
      status: ruleBundleStatus,
      error: nullableString,
      created_at: z.number(),
      sent_at: nullableNumber,
      applied_at: nullableNumber,
    }),
  ),
});
export type RuleEdge = z.infer<typeof ruleEdge>;

export const holiday = z.object({ date: z.string(), name: z.string() });
export type Holiday = z.infer<typeof holiday>;
export const holidayList = z.object({ holidays: z.array(holiday) });

export const siteRules = z.object({
  timezone: z.string(),
  holidays: z.array(holiday),
  edges: z.array(ruleEdge),
  items: z.array(rule),
});
export type SiteRules = z.infer<typeof siteRules>;

export interface RuleBody {
  name: string;
  description?: string | null;
  enabled: boolean;
  definition: RuleDefinition;
}
export interface CreateRuleBody extends RuleBody {
  edge_id: string;
}
export interface UpdateRuleBody extends RuleBody {
  /** The version the editor started from; 409 `stale_rule` when another was saved. */
  base_version?: number;
}

export const ruleEventKind = z.enum(['raised', 'cleared', 'would_write']);
export type RuleEventKind = z.infer<typeof ruleEventKind>;

export const ruleEvent = z.object({
  id: z.string(),
  event_id: z.string(),
  rule_id: z.string(),
  rule_name: nullableString,
  edge_id: z.string(),
  edge_node_id: z.string(),
  kind: ruleEventKind,
  severity: z.nullable(alarmSeverity),
  message: z.string(),
  write: z.nullable(
    z.object({
      point_key: z.string(),
      device_id: z.string(),
      metric: z.string(),
      value: z.union([z.number(), z.boolean(), z.null()]),
      priority: nullableNumber,
    }),
  ),
  at: z.number(),
  received_at: z.number(),
});
export type RuleEvent = z.infer<typeof ruleEvent>;
export const ruleEventList = itemList(ruleEvent);

export interface RuleEventQuery {
  rule_id?: string;
  kind?: RuleEventKind;
  limit?: number;
}

export const rulePoint = z.object({
  point_key: z.string(),
  point_id: nullableString,
  name: nullableString,
  object_type: nullableString,
  unit: nullableString,
  device_id: z.string(),
  metric: z.string(),
  /** AV/BV: allowed as a (dry-run) write target. */
  writable: z.boolean(),
  virtual: z.boolean(),
});
export type RulePoint = z.infer<typeof rulePoint>;
export const rulePointList = itemList(rulePoint);
